/**
 * auditoriaService — Llamadas API para el registro de actividad (logs)
 *
 * Qué hace: Envía peticiones al backend; fallback a localStorage offline.
 *           Además, dispara (best-effort) una notificación local real del
 *           dispositivo para cada evento registrado, según las preferencias
 *           de notificaciones del usuario (ver notifyAuditEvent).
 * Qué exporta: auditoriaService
 */
import { logger } from "@/lib/logger";
import api from "@/services/api";
import { unwrapApiList } from "@/lib/unwrapApiResponse";
import { useAuthStore } from "@/store/authStore";
import type { ClientAuditEntry } from "@/lib/audit/types";
import { Log, LogFilters } from "../types/auditoria.types";
import {
  shouldPreferLocalDemoOps,
  shouldFallbackToLocalStorage,
} from "@/lib/localDemoOps";
import {
  appendLocalAuditLog,
  readLocalAuditLogs,
  updateLocalLogStatus,
  updateLocalLogsStatus,
} from "../utils/localAuditoriaStorage";
import { modernizeLogs } from "../utils/modernizeLogs";
import { notifyAuditEvent } from "../utils/auditLocalNotification";
import { notifyDailyGoalProgress } from "../utils/dailyGoalNotification";

export const auditoriaService = {
  getLogs: async (filters: Partial<LogFilters>): Promise<Log[]> => {
    if (shouldPreferLocalDemoOps()) return readLocalAuditLogs(filters);
    try {
      const queryParams = new URLSearchParams();
      if (filters.userId) queryParams.append("userId", filters.userId);
      if (filters.action) queryParams.append("action", filters.action);
      if (filters.fechaInicio)
        queryParams.append("fechaInicio", filters.fechaInicio);
      if (filters.fechaFin) queryParams.append("fechaFin", filters.fechaFin);

      const { data } = await api.get(
        `auditoria/logs?${queryParams.toString()}`,
      );
      const logs = unwrapApiList<Log>(data, "logs");
      return modernizeLogs(logs);
    } catch (err) {
      if (shouldFallbackToLocalStorage(err)) return readLocalAuditLogs(filters);
      throw err;
    }
  },

  updateLogStatus: async (
    logId: string,
    isViewed: boolean,
  ): Promise<boolean> => {
    if (shouldPreferLocalDemoOps()) {
      updateLocalLogStatus(logId, isViewed);
      return true;
    }
    try {
      await api.patch(`auditoria/logs/${logId}/view`, { isViewed });
      return true;
    } catch (err) {
      if (shouldFallbackToLocalStorage(err)) {
        updateLocalLogStatus(logId, isViewed);
        return true;
      }
      throw err;
    }
  },

  updateLogsStatus: async (
    logIds: string[],
    isViewed: boolean,
  ): Promise<boolean> => {
    if (shouldPreferLocalDemoOps()) {
      updateLocalLogsStatus(logIds, isViewed);
      return true;
    }
    try {
      await api.patch(`auditoria/logs/view-batch`, { logIds, isViewed });
      return true;
    } catch (err) {
      if (shouldFallbackToLocalStorage(err)) {
        updateLocalLogsStatus(logIds, isViewed);
        return true;
      }
      throw err;
    }
  },

  recordLog: async (payload: {
    userId: string;
    userName: string;
    action: string;
    entidad: string;
    entidadId?: string;
    resumen?: string;
    descripcion?: string;
    severity?: "low" | "medium" | "high";
    endpoint?: string;
    sessionId?: string;
    ipAddress?: string;
    userAgent?: string;
    details?: Record<string, unknown>;
    cambios?: import("../types/auditoria.types").AuditChange[];
  }): Promise<void> => {
    // Captura de datos de seguridad para auditoría (Simulado para demo)
    const mockLocalIp = "192.168.1.45"; // Simula IP de red interna
    const userAgent =
      typeof window !== "undefined" ? window.navigator.userAgent : "Server";

    const logPayload = {
      ...payload,
      ipAddress: payload.ipAddress || mockLocalIp,
      userAgent: payload.userAgent || userAgent,
      cambios: payload.cambios || [],
      details: {
        ...(payload.details || {}),
        userAgent: payload.userAgent || userAgent,
      },
    };

    if (shouldPreferLocalDemoOps()) {
      const createdLog = appendLocalAuditLog(logPayload);
      notifyAuditEvent(logPayload, createdLog.id);
      notifyDailyGoalProgress(logPayload.action);
      return;
    }
    try {
      await api.post("auditoria/logs", payload);
      notifyAuditEvent(logPayload);
      notifyDailyGoalProgress(logPayload.action);
    } catch (err) {
      if (shouldFallbackToLocalStorage(err)) {
        const createdLog = appendLocalAuditLog(logPayload);
        notifyAuditEvent(logPayload, createdLog.id);
        notifyDailyGoalProgress(logPayload.action);
        return;
      }
      throw err;
    }
  },

  /* ── Acciones de Mitigación / Respuesta Rápida ── */

  /**
   * Revoca una sesión activa.
   * Simulamos éxito inmediato para efectos de la demo.
   */
  revokeSession: async (sessionId: string): Promise<boolean> => {
    logger.log(`[Service] Revocando sesión: ${sessionId}`);
    // Simulamos un retraso de red
    await new Promise((resolve) => setTimeout(resolve, 800));
    return true;
  },

  /**
   * Bloque IP del cliente.
   */
  blockIp: async (ip: string): Promise<boolean> => {
    logger.log(`[Service] Bloqueando IP: ${ip}`);
    await new Promise((resolve) => setTimeout(resolve, 800));
    return true;
  },

  /**
   * Genera un acta administrativa para un registro de caja.
   */
  generateActa: async (
    logId: string,
  ): Promise<{ success: boolean; url?: string }> => {
    logger.log(`[Service] Generando acta para log: ${logId}`);
    await new Promise((resolve) => setTimeout(resolve, 1200));
    return { success: true, url: "/docs/acta-generada.pdf" };
  },

  /**
   * Notifica a un empleado sobre un evento de auditor��a.
   */
  notifyEmployee: async (userId: string, message: string): Promise<boolean> => {
    logger.log(`[Service] Notificando a ${userId}: ${message}`);
    await new Promise((resolve) => setTimeout(resolve, 600));
    return true;
  },
};

/**
 * recordClientAudit — CONTRATO PÚBLICO del módulo de auditoría para cualquier
 * feature: registra una acción (mock MSW / futuro API). Es transversal y se
 * importa desde aquí (service público), nunca desde utils internos.
 */
export async function recordClientAudit(
  entry: ClientAuditEntry,
): Promise<void> {
  const user = useAuthStore.getState().user;
  try {
    await auditoriaService.recordLog({
      userId: user?.id || "unknown",
      userName: user?.nombre || "Usuario",
      action: entry.action,
      entidad: entry.entidad,
      entidadId: entry.entidadId,
      resumen: entry.resumen || "",
      descripcion: entry.descripcion || entry.resumen || "",
      severity: entry.severity || "medium",
      endpoint: `CLIENT ${entry.entidad}`,
      details: entry.details,
      cambios: entry.cambios,
    });
  } catch {
    /* demo: no bloquear flujo si falla auditor��a */
  }
}
